import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, Plus, Share2, Filter, Calendar, Info, Ban, Check, User } from 'lucide-react';
import './DelegationsTab.css';
import {
  AdminEmployeeListItem, EmployeeLite, getApiErrorMessage,
} from '../../../api/adminManagement';
import {
  DelegationView, CreateDelegationInput,
  DELEGATION_TYPE_SUPERVISOR_TREE, DELEGATION_TYPE_INDIVIDUAL, DelegationType,
  getApproverDelegations, createApproverDelegation, setApproverDelegationActive,
  getDelegationSubtreeSize,
} from '../../../api/approverDelegations';
import {
  Avatar, StatusPill, StatusVariant, SegmentedControl, SearchablePicker, MultiSelectPicker,
  PickerOption, PreviewLine, ErrorBanner, InlineError, ModalShell,
} from './AdminManagementKit';
import { sortSupervisorOptions } from '../../../utils/supervisorPickerOptions';

/**
 * Tab C — Delegations (Approval Access WP9c). Delegate-centric: grants are grouped
 * by delegate admin. Statuses (Active / Scheduled / Expired / Revoked) are derived
 * client-side from the active flag + the date window using the SAME date-only
 * semantics as the server (see deriveStatus). The tab loads its own delegation list
 * (WP3 GET) and, after any mutation, refreshes both the list and — via onMutated —
 * Tab A's delegation count chips.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Local calendar day as 'YYYY-MM-DD' (lexically comparable to the server's date strings). */
function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 'YYYY-MM-DD' → 'Jul 15, 2026'. */
function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}
/** 'YYYY-MM-DD' → 'Jul 15' (no year — used for the start of a same-year range). */
function fmtDateShort(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}

/** Human date-window label per the README: Permanent / From … / … – … / Until …. */
function dateText(startDate: string | null, endDate: string | null): string {
  if (!startDate && !endDate) return 'Permanent';
  if (startDate && !endDate) return `From ${fmtDate(startDate)}`;
  if (!startDate && endDate) return `Until ${fmtDate(endDate)}`;
  const sameYear = startDate!.slice(0, 4) === endDate!.slice(0, 4);
  return sameYear
    ? `${fmtDateShort(startDate!)} – ${fmtDate(endDate!)}`
    : `${fmtDate(startDate!)} – ${fmtDate(endDate!)}`;
}

/**
 * Derive the display status using the server's date-window semantics (spec §12,
 * D5): inactive ⇒ Revoked; active + start in the future ⇒ Scheduled; active + end
 * in the past ⇒ Expired; otherwise Active. Date-only comparison.
 */
function deriveStatus(d: DelegationView): StatusVariant {
  if (!d.active) return 'revoked';
  const today = todayISO();
  if (d.startDate && d.startDate > today) return 'scheduled';
  if (d.endDate && d.endDate < today) return 'expired';
  return 'active';
}

function firstLast(name: string | null | undefined): { first: string; last: string } {
  const parts = (name ?? '').trim().split(/\s+/);
  return { first: parts[0] ?? '', last: parts.length > 1 ? parts[parts.length - 1] : '' };
}

const CURRENT_STATUSES: StatusVariant[] = ['active', 'scheduled'];

type StatusFilter = 'current' | 'all';
type GrantType = DelegationType;

interface Props {
  admins: AdminEmployeeListItem[];
  employees: EmployeeLite[];
  /** Deep-link from a Tab A delegation chip: show the amber banner + highlight the group. */
  deepLinkDelegate: { adminEmployeeID: number; name: string } | null;
  onClearDeepLink: () => void;
  /** Refresh Tab A's delegation count chips after a mutation (page's reloadAdmins). */
  onMutated: () => void;
  showToast: (msg: string, isError?: boolean) => void;
}

const DelegationsTab: React.FC<Props> = ({
  admins, employees, deepLinkDelegate, onClearDeepLink, onMutated, showToast,
}) => {
  const [delegations, setDelegations] = useState<DelegationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('current');

  // Add modal state
  const [addOpen, setAddOpen] = useState(false);

  // Revoke confirm state
  const [revokeTarget, setRevokeTarget] = useState<DelegationView | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  // Row highlight (points a duplicate-active error at the offending existing row).
  const [highlightRowID, setHighlightRowID] = useState<number | null>(null);
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Subtree sizes for SUPERVISOR_TREE grant rows ("+ their entire team (N employees)").
  // Fetched lazily per distinct supervisor; a ref tracks requested IDs so a failed
  // fetch degrades to the generic copy instead of retrying in a loop.
  const [subtreeSizes, setSubtreeSizes] = useState<Record<number, number>>({});
  const requestedSizes = useRef<Set<number>>(new Set());

  useEffect(() => {
    const ids = Array.from(new Set(
      delegations
        .filter((d) => d.delegationType === DELEGATION_TYPE_SUPERVISOR_TREE && d.delegatedSupervisorEmployeeID != null)
        .map((d) => d.delegatedSupervisorEmployeeID as number),
    )).filter((id) => !requestedSizes.current.has(id));
    if (ids.length === 0) return;
    ids.forEach((id) => requestedSizes.current.add(id));
    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        ids.map(async (id) => ({ id, count: await getDelegationSubtreeSize(id) })),
      );
      if (cancelled) return;
      const next: Record<number, number> = {};
      results.forEach((r) => { if (r.status === 'fulfilled') next[r.value.id] = r.value.count; });
      if (Object.keys(next).length > 0) setSubtreeSizes((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [delegations]);

  // ── Load ─────────────────────────────────────────────────────────────────────
  const reloadDelegations = useCallback(async () => {
    // Always fetch inactive too so the "All statuses" view + audit history are complete;
    // client-side filtering hides them under the default "Current" filter.
    try {
      const rows = await getApproverDelegations({ includeInactive: true });
      setDelegations(rows);
      setLoadError(null);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, 'Failed to load delegations.'));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await getApproverDelegations({ includeInactive: true });
        if (!cancelled) { setDelegations(rows); setLoadError(null); }
      } catch (err) {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Failed to load delegations.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Scroll the highlighted row into view when it is set (duplicate-active pointer).
  useEffect(() => {
    if (highlightRowID == null) return;
    const el = rowRefs.current[highlightRowID];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = window.setTimeout(() => setHighlightRowID(null), 2600);
    return () => window.clearTimeout(t);
  }, [highlightRowID]);

  // ── Filtering + grouping ───────────────────────────────────────────────────────
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return delegations.filter((d) => {
      if (deepLinkDelegate && d.delegateAdminEmployeeID !== deepLinkDelegate.adminEmployeeID) return false;
      const status = deriveStatus(d);
      if (statusFilter === 'current' && !CURRENT_STATUSES.includes(status)) return false;
      if (q) {
        const hay = `${d.delegateAdminName ?? ''} ${d.targetName ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [delegations, search, statusFilter, deepLinkDelegate]);

  const groups = useMemo(() => {
    const map = new Map<number, { name: string; role: string | null; rows: DelegationView[] }>();
    const order: number[] = [];
    visible.forEach((d) => {
      let g = map.get(d.delegateAdminEmployeeID);
      if (!g) {
        g = { name: d.delegateAdminName ?? `Admin #${d.delegateAdminEmployeeID}`, role: d.delegateAdminRoleName, rows: [] };
        map.set(d.delegateAdminEmployeeID, g);
        order.push(d.delegateAdminEmployeeID);
      }
      g.rows.push(d);
    });
    return order.map((id) => ({ id, ...map.get(id)! }));
  }, [visible]);

  // Whether any delegations exist at all (drives the first-run empty state vs. no-match).
  const hasAny = delegations.length > 0;

  // ── Revoke ─────────────────────────────────────────────────────────────────────
  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeBusy(true);
    try {
      await setApproverDelegationActive(revokeTarget.employeeApproverDelegationID, false);
      setRevokeTarget(null);
      showToast('Delegation revoked. It stays in the list as Revoked for audit.');
      await reloadDelegations();
      onMutated();
    } catch (err) {
      showToast(getApiErrorMessage(err), true);
      setRevokeTarget(null);
    } finally {
      setRevokeBusy(false);
    }
  };

  const revokeTargetPhrase = revokeTarget
    ? (revokeTarget.delegationType === DELEGATION_TYPE_SUPERVISOR_TREE
      ? `${revokeTarget.targetName ?? 'the target'}’s team`
      : (revokeTarget.targetName ?? 'the target'))
    : '';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Controls bar */}
      <div className="toolbar">
        <div className="field search">
          <span className="flab">Search</span>
          <div className="searchwrap">
            <Search />
            <input
              className="input"
              placeholder="Search by delegate or target…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <span className="flab">Status</span>
          <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
            <option value="current">Current (active &amp; scheduled)</option>
            <option value="all">All statuses</option>
          </select>
        </div>
        <div className="field push">
          <button className="btn primary" type="button" onClick={() => setAddOpen(true)}><Plus />Add Delegation</button>
        </div>
      </div>

      {/* Deep-link filter banner */}
      {deepLinkDelegate && (
        <div className="deleg-filter-banner">
          <Filter />
          <span>Showing delegations for <b>{deepLinkDelegate.name}</b></span>
          <button className="linkbtn" type="button" onClick={onClearDeepLink}>Clear filter</button>
        </div>
      )}

      {/* List / empty states */}
      {loading ? (
        <div className="load-note"><span className="am-spin" /> Loading delegations…</div>
      ) : loadError ? (
        <div className="card"><div className="empty"><div className="et">Couldn't load delegations</div><p>{loadError}</p></div></div>
      ) : groups.length === 0 ? (
        deepLinkDelegate ? (
          <div className="card">
            <div className="empty">
              <div className="eic"><Share2 /></div>
              <div className="et">No delegations for {deepLinkDelegate.name}</div>
              <p>Add one, or clear the filter to see all delegations.</p>
              <button className="btn primary" type="button" onClick={() => setAddOpen(true)} style={{ marginTop: 6 }}><Plus />Add delegation</button>
            </div>
          </div>
        ) : hasAny ? (
          <div className="card">
            <div className="empty">
              <div className="eic"><Share2 /></div>
              <div className="et">No delegations match</div>
              <p>Adjust the search or status filter to see more.</p>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="empty">
              <div className="eic"><Share2 /></div>
              <div className="et">No delegations yet</div>
              <p>Delegations let another admin cover approvals for employees outside their own team — for vacations, leaves, or org quirks.</p>
              <button className="btn primary" type="button" onClick={() => setAddOpen(true)} style={{ marginTop: 6 }}><Plus />Add delegation</button>
            </div>
          </div>
        )
      ) : (
        groups.map((g) => {
          const isFiltered = deepLinkDelegate?.adminEmployeeID === g.id;
          const who = firstLast(g.name);
          return (
            <div key={g.id} className={`deleg-group${isFiltered ? ' filtered' : ''}`}>
              <div className="dg-head">
                <Avatar first={who.first} last={who.last} sm />
                <div>
                  <div className="nm">{g.name}</div>
                  {g.role && <div className="dg-role">{g.role}</div>}
                </div>
                <span className="dg-meta">{g.rows.length} grant{g.rows.length === 1 ? '' : 's'}</span>
              </div>
              <div className="dg-body">
                {g.rows.map((d) => {
                  const status = deriveStatus(d);
                  const isTree = d.delegationType === DELEGATION_TYPE_SUPERVISOR_TREE;
                  const canRevoke = status === 'active' || status === 'scheduled';
                  const dim = status === 'revoked' || status === 'expired';
                  return (
                    <div
                      key={d.employeeApproverDelegationID}
                      ref={(el) => { rowRefs.current[d.employeeApproverDelegationID] = el; }}
                      className={`deleg-row${dim ? ` ${status}-row` : ''}${highlightRowID === d.employeeApproverDelegationID ? ' filtered' : ''}`}
                    >
                      {isTree ? (
                        <span className="type-chip tree"><Share2 />Supervisor tree</span>
                      ) : (
                        <span className="type-chip individual"><User />Individual</span>
                      )}
                      <div className="dr-target">
                        {d.targetName ?? '—'}
                        {isTree && (() => {
                          const n = d.delegatedSupervisorEmployeeID != null
                            ? subtreeSizes[d.delegatedSupervisorEmployeeID]
                            : undefined;
                          return (
                            <span className="sub">
                              + their entire team{typeof n === 'number' ? ` (${n} employee${n === 1 ? '' : 's'})` : ''}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="dr-dates"><Calendar />{dateText(d.startDate, d.endDate)}</div>
                      <StatusPill variant={status} />
                      <div className="dr-act">
                        {canRevoke
                          ? <button className="linkbtn danger" type="button" onClick={() => setRevokeTarget(d)}>Revoke</button>
                          : <span className="dash">—</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {/* Add Delegation modal */}
      {addOpen && (
        <AddDelegationModal
          admins={admins}
          employees={employees}
          existing={delegations}
          onClose={() => setAddOpen(false)}
          onCreated={async (msg) => {
            setAddOpen(false);
            showToast(msg);
            await reloadDelegations();
            onMutated();
          }}
          onPointAtRow={(id) => setHighlightRowID(id)}
        />
      )}

      {/* Revoke confirm (red, soft) */}
      <ModalShell
        open={!!revokeTarget}
        onClose={() => { if (!revokeBusy) setRevokeTarget(null); }}
        variant="danger"
        icon={<Ban />}
        title="Revoke delegation?"
        subtitle="Effective immediately"
        footer={
          <>
            <button className="btn ghost" type="button" onClick={() => setRevokeTarget(null)} disabled={revokeBusy}>Cancel</button>
            <button className="btn danger-solid" type="button" onClick={confirmRevoke} disabled={revokeBusy}>
              {revokeBusy ? 'Working…' : 'Revoke'}
            </button>
          </>
        }
      >
        <div className="modal-confirm-body">
          <b>{revokeTarget?.delegateAdminName}</b> will lose access to <b>{revokeTargetPhrase}</b>. The grant is
          kept in the list as <b>Revoked</b> for audit — it is not deleted.
        </div>
      </ModalShell>
    </>
  );
};

// ── Add Delegation modal ─────────────────────────────────────────────────────
interface AddProps {
  admins: AdminEmployeeListItem[];
  employees: EmployeeLite[];
  existing: DelegationView[];
  onClose: () => void;
  onCreated: (toastMsg: string) => void | Promise<void>;
  onPointAtRow: (employeeApproverDelegationID: number) => void;
}

const SELF_DELEGATION_MSG = "An admin can't be delegated their own team.";

const AddDelegationModal: React.FC<AddProps> = ({ admins, employees, existing, onClose, onCreated, onPointAtRow }) => {
  const [delegateID, setDelegateID] = useState<number | null>(null);
  const [grantType, setGrantType] = useState<GrantType>(DELEGATION_TYPE_SUPERVISOR_TREE);
  const [supervisorID, setSupervisorID] = useState<number | null>(null);
  const [employeeIDs, setEmployeeIDs] = useState<number[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [targetError, setTargetError] = useState<string | null>(null);
  const [blockingError, setBlockingError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Picker data sources ---------------------------------------------------------
  // Delegate: ACTIVE admins (roster). Supervisor + individual pickers: ALL ACTIVE
  // employees — the server validates the target (exists/active/self/duplicate), and
  // "who is a supervisor" isn't cheaply derivable client-side, so offering all active
  // employees is the documented acceptable choice.
  const delegateOptions: PickerOption[] = useMemo(
    // Employee-only logins (no admin role, TAS-667) can't approve, so they can't be delegates.
    () => admins.filter((a) => a.active && a.adminEmployeeRoleID !== null).map((a) => ({
      id: a.adminEmployeeID,
      name: `${a.name.first} ${a.name.last}`.trim(),
      sub: a.email,
    })),
    [admins],
  );
  // SearchablePicker already matches on `sub`, so the EE# is searchable here regardless;
  // the EE#-first flag only reorders the list (numerically ascending, unnumbered last).
  const employeeOptions: PickerOption[] = useMemo(
    () => sortSupervisorOptions(
      employees.filter((e) => e.active).map((e) => ({
        id: e.employeeID,
        name: `${e.firstName} ${e.lastName}`.trim(),
        sub: e.altEmployeeID ? `#${e.altEmployeeID}` : undefined,
        altEmployeeID: e.altEmployeeID,
      })),
    ).map(({ id, name, sub }) => ({ id, name, sub })),
    [employees],
  );

  const delegate = admins.find((a) => a.adminEmployeeID === delegateID) ?? null;
  const delegateName = delegate ? `${delegate.name.first} ${delegate.name.last}`.trim() : '';
  const supervisor = employees.find((e) => e.employeeID === supervisorID) ?? null;
  const supervisorName = supervisor ? `${supervisor.firstName} ${supervisor.lastName}`.trim() : '';

  // Subtree size for the selected supervisor (WP4c endpoint) — null while loading
  // or on failure, in which case the preview degrades to the generic copy.
  const [treeSize, setTreeSize] = useState<number | null>(null);
  useEffect(() => {
    setTreeSize(null);
    if (supervisorID == null) return;
    let cancelled = false;
    getDelegationSubtreeSize(supervisorID)
      .then((n) => { if (!cancelled) setTreeSize(n); })
      .catch(() => { /* degrade to generic preview copy */ });
    return () => { cancelled = true; };
  }, [supervisorID]);

  // Client-side self-delegation mirror (server is authoritative): supervisor target
  // equals the delegate's own linked employee.
  const selfDelegation = grantType === DELEGATION_TYPE_SUPERVISOR_TREE
    && !!delegate?.employeeID
    && supervisorID === delegate.employeeID;

  const dateInvalid = !!startDate && !!endDate && endDate < startDate;

  const targetChosen = grantType === DELEGATION_TYPE_SUPERVISOR_TREE ? supervisorID != null : employeeIDs.length > 0;
  const canSubmit = delegateID != null && targetChosen && !selfDelegation && !dateInvalid && !saving;

  // Confirm summary sentence ----------------------------------------------------
  const datePhrase = (() => {
    if (!startDate && !endDate) return '';
    if (startDate && !endDate) return ` starting ${fmtDate(startDate)}`;
    if (!startDate && endDate) return ` until ${fmtDate(endDate)}`;
    const sameYear = startDate.slice(0, 4) === endDate.slice(0, 4);
    return sameYear ? ` from ${fmtDateShort(startDate)} – ${fmtDate(endDate)}` : ` from ${fmtDate(startDate)} – ${fmtDate(endDate)}`;
  })();
  const targetPhrase = grantType === DELEGATION_TYPE_SUPERVISOR_TREE
    ? (supervisorName ? `${supervisorName}’s team` : '')
    : (employeeIDs.length === 1
      ? (employeeOptions.find((o) => o.id === employeeIDs[0])?.name ?? '')
      : `${employeeIDs.length} employees`);
  const showConfirm = delegateName !== '' && targetPhrase !== '' && !selfDelegation;

  /** Finds an existing active-window grant matching a would-be duplicate, to point at. */
  const findExistingActive = (delType: GrantType, targetEmployeeID: number): DelegationView | undefined =>
    existing.find((d) => {
      if (d.delegateAdminEmployeeID !== delegateID) return false;
      if (d.delegationType !== delType) return false;
      const t = delType === DELEGATION_TYPE_SUPERVISOR_TREE ? d.delegatedSupervisorEmployeeID : d.delegatedEmployeeID;
      const status = deriveStatus(d);
      return t === targetEmployeeID && (status === 'active' || status === 'scheduled');
    });

  const handleSubmit = async () => {
    if (delegateID == null) return;
    if (selfDelegation) { setTargetError(SELF_DELEGATION_MSG); return; }
    setSaving(true);
    setTargetError(null);
    setBlockingError(null);

    const start = startDate || null;
    const end = endDate || null;

    // Build one create input per target (tree = 1; individual = 1 per employee).
    const inputs: CreateDelegationInput[] = grantType === DELEGATION_TYPE_SUPERVISOR_TREE
      ? [{ delegateAdminEmployeeID: delegateID, delegationType: DELEGATION_TYPE_SUPERVISOR_TREE, delegatedSupervisorEmployeeID: supervisorID!, startDate: start, endDate: end }]
      : employeeIDs.map((id) => ({ delegateAdminEmployeeID: delegateID, delegationType: DELEGATION_TYPE_INDIVIDUAL, delegatedEmployeeID: id, startDate: start, endDate: end }));

    const results = await Promise.allSettled(inputs.map((i) => createApproverDelegation(i)));
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    const successes = results.length - failures.length;

    if (failures.length === 0) {
      setSaving(false);
      await onCreated(successes === 1 ? 'Delegation created.' : `${successes} delegations created.`);
      return;
    }

    // Surface the first failure to the right place; note partial success for batches.
    const firstMsg = getApiErrorMessage(failures[0].reason);
    setSaving(false);
    if (/self-delegation|own (hierarchy|team)/i.test(firstMsg)) {
      setTargetError(SELF_DELEGATION_MSG);
    } else if (/already exists|duplicate/i.test(firstMsg)) {
      // Point at the offending existing row (behind the modal) when we can find it.
      const failedIdx = results.findIndex((r) => r.status === 'rejected');
      const failedInput = inputs[failedIdx];
      const targetEmp = failedInput.delegatedSupervisorEmployeeID ?? failedInput.delegatedEmployeeID;
      const row = targetEmp != null ? findExistingActive(grantType, targetEmp) : undefined;
      setBlockingError(
        row
          ? 'This delegate already has an active grant for that target (highlighted below). Revoke it first or pick a different target.'
          : firstMsg,
      );
      if (row) onPointAtRow(row.employeeApproverDelegationID);
    } else {
      setBlockingError(firstMsg);
    }

    if (successes > 0) {
      // Some of a batch went through — refresh the list so the created ones show.
      await onCreated(`${successes} of ${results.length} delegations created; ${failures.length} could not be created.`);
    }
  };

  return (
    <ModalShell
      open
      onClose={() => { if (!saving) onClose(); }}
      wide
      icon={<Share2 />}
      title="Add delegation"
      subtitle="Grant an admin extra approval access"
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn primary" type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? 'Saving…' : <><Check />Create delegation</>}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {blockingError && <ErrorBanner>{blockingError}</ErrorBanner>}

        {/* 1 · Delegate */}
        <div className="frow">
          <label>1 · Delegate<span className="req">*</span></label>
          <SearchablePicker
            options={delegateOptions}
            value={delegateID}
            onChange={(id) => { setDelegateID(id === null ? null : Number(id)); setTargetError(null); }}
            placeholder="Search active admins…"
          />
        </div>

        {/* 2 · Grant type */}
        <div className="frow">
          <label>2 · Grant type</label>
          <SegmentedControl<GrantType>
            options={[
              { value: DELEGATION_TYPE_SUPERVISOR_TREE, label: "Supervisor's full team", icon: Share2 },
              { value: DELEGATION_TYPE_INDIVIDUAL, label: 'Individual employees', icon: User },
            ]}
            value={grantType}
            onChange={(v) => { setGrantType(v); setTargetError(null); }}
          />
        </div>

        {/* Target picker (varies by grant type) */}
        {grantType === DELEGATION_TYPE_SUPERVISOR_TREE ? (
          <div className="frow">
            <label>Supervisor<span className="req">*</span></label>
            <SearchablePicker
              options={employeeOptions}
              value={supervisorID}
              onChange={(id) => { setSupervisorID(id === null ? null : Number(id)); setTargetError(null); }}
              placeholder="Search supervisors…"
            />
            {targetError && <InlineError>{targetError}</InlineError>}
            {selfDelegation && <InlineError>{SELF_DELEGATION_MSG}</InlineError>}
            {supervisorName && !selfDelegation && (
              <PreviewLine>
                Grants access to <b>{supervisorName}</b>{treeSize != null
                  ? <> and the <b>{treeSize}</b> employee{treeSize === 1 ? '' : 's'} under them.</>
                  : ' and their entire team.'}
              </PreviewLine>
            )}
          </div>
        ) : (
          <div className="frow">
            <label>Employees <span className="opt">— select one or more</span></label>
            <MultiSelectPicker
              options={employeeOptions}
              values={employeeIDs}
              onChange={(ids) => { setEmployeeIDs(ids.map(Number)); setTargetError(null); }}
              placeholder="Search employees to add…"
            />
            {targetError && <InlineError>{targetError}</InlineError>}
          </div>
        )}

        {/* 3 · Dates */}
        <div className="frow">
          <label>3 · Dates <span className="opt">— leave both blank for permanent access</span></label>
          <div className="frow2">
            <input className="input" type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} aria-label="Start date" />
            <input className="input" type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} aria-label="End date" />
          </div>
          {dateInvalid && <InlineError>End date must be on or after the start date.</InlineError>}
          <div className="dates-note"><Info />Both blank = <b>Permanent</b></div>
        </div>

        {/* Persistent reminder: role-limits + non-transitive (exact README copy). */}
        <PreviewLine>
          What a delegate can <b>do</b> with this access is still limited by their role's permissions.
          Delegations are <b>not transitive</b> — they grant the supervisor's team only, not delegations
          that supervisor has received.
        </PreviewLine>

        {/* Confirm summary sentence */}
        {showConfirm && (
          <div className="confirm-summary">
            <b>{delegateName}</b> will be able to view and approve for <b>{targetPhrase}</b>{datePhrase}.
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default DelegationsTab;
